import {Component, computed, effect, inject, input, output, signal, untracked} from '@angular/core';
import {TranslocoPipe} from '@jsverse/transloco';
import {Tab, TabList, TabPanel, TabPanels, Tabs} from 'primeng/tabs';
import {Card} from 'primeng/card';
import {Button} from 'primeng/button';
import {Router} from '@angular/router';
import {APP_ROUTES} from '../../../../../app.routes';
import {TenderDetailResponse, TenderPhaseType, TenderStatus} from '@frontend/shared-api';
import {TabStatusEnum} from '../../../../../shared/tabs/tab-status.model';
import {TenderInvitationMailComponent} from '../../components/tender-invitation-mail/tender-invitation-mail.component';
import {TenderDemonstrationAgendaComponent} from '../tender-demonstration-agenda/tender-demonstration-agenda.component';
import {TenderDecisionComponent} from '../../components/tender-decision/tender-decision.component';
import {WizardPhaseHeaderComponent} from '../../components/wizard-phase-header/wizard-phase-header.component';
import {TAB_INDICES} from './rfd-tender-wizard.types';
import {createTabManagementSignals} from '../../../../../shared/tabs/tab-utils';
import {TabStatusDirective} from '../../../../../shared/tabs/tab-status.directive';
import {TabsUnsavedRevealDirective} from '../../../../../shared/tabs/tabs-unsaved-reveal.directive';

@Component({
  selector: 'app-rfd-tender-wizard',
  imports: [
    TranslocoPipe,
    Tabs,
    TabList,
    Tab,
    TabPanels,
    Card,
    TabPanel,
    Button,
    TenderInvitationMailComponent,
    TenderDemonstrationAgendaComponent,
    TenderDecisionComponent,
    WizardPhaseHeaderComponent,
    TabStatusDirective,
    TabsUnsavedRevealDirective,
  ],
  templateUrl: './rfd-tender-wizard.component.html',
})
export class RfdTenderWizardComponent {
  private readonly router = inject(Router);

  readonly tenderDetails = input.required<TenderDetailResponse>();
  readonly openDecision = input(false);

  readonly accepted = output<void>();
  readonly rejected = output<void>();

  protected readonly tenderId = computed(() => this.tenderDetails().tenderId);

  protected readonly tabManagement = createTabManagementSignals();
  protected readonly mailPreviewTabStatus = signal(TabStatusEnum.COMPLETED);
  protected readonly demonstrationAgendaTabStatus = signal(TabStatusEnum.COMPLETED);
  protected readonly decisionTabStatus = signal(TabStatusEnum.NOT_STARTED);

  protected readonly tenderPhase = TenderPhaseType.RFD;
  protected readonly questionnaireInvited = computed(() => {
    const tenderDetails = this.tenderDetails();
    return tenderDetails.status === TenderStatus.INVITED;
  });

  constructor() {
    effect(() => {
      if (this.openDecision() && this.questionnaireInvited()) {
        untracked(() => this.onTabChange(TAB_INDICES.DECISION));
      }
    });
  }

  get activeTabIndex() {
    return this.tabManagement.activeTabIndex.asReadonly();
  }

  onTabChange(tabIndex: string | number): void {
    this.tabManagement.onTabChange(tabIndex);
  }

  goToNextTab(): void {
    const currentIndex = this.activeTabIndex();
    if (currentIndex === TAB_INDICES.MAIL_PREVIEW) {
      this.onTabChange(TAB_INDICES.DEMONSTRATION_AGENDA);
    } else if (currentIndex === TAB_INDICES.DEMONSTRATION_AGENDA) {
      this.onTabChange(TAB_INDICES.DECISION);
    }
  }

  onTenderAccepted(): void {
    this.router.navigate([APP_ROUTES.PROVIDER_TENDERS]);
  }
}
